// =============================================================================
// AUTH CONTEXT
// Provides authentication state throughout the app
// =============================================================================

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, signIn, signOut, signUp } from '../lib/supabase';
import { logger } from '../utils/logger';

const log = logger.module('AuthContext');

interface Profile {
  id: string;
  email?: string;
  role?: 'admin' | 'owner' | 'operations' | 'sales' | 'partner' | 'client';
  name?: string;
  [key: string]: any;
}

interface AuthResponse {
  success: boolean;
  data?: any;
  error?: any;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<AuthResponse>;
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  signOut: () => Promise<AuthResponse>;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  isPartner: () => boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// PROFILE CACHE - Persist last-known-good profile to survive outages
// =============================================================================
// In-memory profile cache (session-only, Supabase is source of truth)
let profileCache: { id: string; profile: Profile; cachedAt: string } | null = null;

function getCachedProfile(userId: string): Profile | null {
  if (profileCache && profileCache.id === userId && profileCache.cachedAt) {
    const cacheAge = Date.now() - new Date(profileCache.cachedAt).getTime();
    const oneHour = 60 * 60 * 1000;
    if (cacheAge < oneHour) {
      log.debug('Using cached profile from memory (cache age:', Math.round(cacheAge / 1000 / 60), 'min)');
      return profileCache.profile;
    } else {
      log.debug('Profile cache expired, will fetch fresh');
      clearCachedProfile();
    }
  }
  return null;
}

function setCachedProfile(userId: string, profile: Profile): void {
  profileCache = {
    id: userId,
    profile,
    cachedAt: new Date().toISOString()
  };
}

function clearCachedProfile(): void {
  profileCache = null;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const profileCache = useRef<Map<string, Profile>>(new Map());
  const [sessionReady, setSessionReady] = useState(false);
  const [resolvedProfileId, setResolvedProfileId] = useState<string | null>(null);

  // Fetch user profile from profiles table with caching and timeout
  const fetchProfile = async (userId: string): Promise<Profile | null> => {
    // Check memory cache first
    if (profileCache.current.has(userId)) {
      log.debug('Profile loaded from memory cache');
      return profileCache.current.get(userId)!;
    }
    
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      log.debug('Fetching profile for user:', userId);
      
      // Add 5 second timeout — generous enough for slow networks, fast enough
      // to fail visibly rather than leaving the user staring at a blank screen
      const timeoutPromise = new Promise<null>((_, reject) => {
        timeout = setTimeout(() => reject(new Error('Profile fetch timeout')), 5000);
      });
      
      const fetchPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      const result = await Promise.race([fetchPromise, timeoutPromise]) as any;
      
      // Handle the result
      const { data, error } = result || {};
      
      if (error) {
        log.error('Error fetching profile:', error);
        
        // Try localStorage cache before falling back to restricted role
        const cachedProfile = getCachedProfile(userId);
        if (cachedProfile) {
          log.warn('Using cached profile due to fetch error');
          profileCache.current.set(userId, cachedProfile);
          return cachedProfile;
        }
        
        // If it's a 500 error or permission error, create a restricted fallback profile
        if (error.code === '500' || error.message?.includes('500')) {
          log.warn('Supabase 500 error - no cached profile available. Creating restricted fallback.');
          // SECURITY: Default to 'client' role - never grant admin access on error
          const fallbackProfile: Profile = {
            id: userId,
            email: user?.email,
            role: 'client',
            name: user?.email?.split('@')[0],
            _fallback: true // Mark as fallback for UI indication
          };
          return fallbackProfile;
        }
        
        // If profile doesn't exist, return null
        if (error.code === 'PGRST116') {
          log.warn('Profile not found, user needs to create profile');
          return null;
        }
        return null;
      }
      
      // Cache the profile (memory + localStorage)
      if (data) {
        log.debug('Profile fetched successfully:', data.role);
        profileCache.current.set(userId, data);
        setCachedProfile(userId, data); // Persist to localStorage
      }
      
      return data;
    } catch (err: any) {
      // Try localStorage cache on any error
      const cachedProfile = getCachedProfile(userId);
      if (cachedProfile) {
        log.warn('Using cached profile due to error:', err?.message);
        profileCache.current.set(userId, cachedProfile);
        return cachedProfile;
      }
      
      // Timeouts are expected when Supabase is slow - use warn, not error
      if (err?.message?.includes('timeout')) {
        log.warn('Profile fetch timeout - no cached profile available');
      } else {
        log.warn('Profile fetch issue:', err?.message || err);
      }
      // Return null so loading stops and user sees error/login
      return null;
    } finally {
      clearTimeout(timeout);
    }
  };

  // Auth listeners must return synchronously. Profile requests run in a
  // separate effect after the auth client has released its session lock.
  useEffect(() => {
    let active = true;
    let receivedEvent = false;
    const applySession = (nextUser: User | null) => {
      if (!active) return;
      setUser(nextUser);
      setSessionReady(true);
      setLoading(false);
      if (!nextUser) {
        setProfile(null);
        setResolvedProfileId(null);
        profileCache.current.clear();
        clearCachedProfile();
      }
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      receivedEvent = true;
      applySession(session?.user ?? null);
    });
    // Do not let an older bootstrap response overwrite a newer sign-in/out.
    void supabase.auth.getSession().then(({ data: { session }, error: sessionError }) => {
      if (!active || receivedEvent) return;
      if (sessionError) setError('Unable to verify your session. Please reload to try again.');
      applySession(session?.user ?? null);
    }).catch(() => {
      if (!active || receivedEvent) return;
      setError('Unable to verify your session. Please reload to try again.');
      applySession(null);
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!user) return;
    let active = true;
    setProfile(null);
    setResolvedProfileId(null);
    void fetchProfile(user.id).then(userProfile => {
      if (!active) return;
      setProfile(userProfile ?? {
        id: user.id, email: user.email, role: 'client', _fallback: true,
      });
      setResolvedProfileId(user.id);
    });
    // Ignore requests that finish after sign-out or an account change.
    return () => { active = false; };
  }, [user?.id]);

  // Sign up function
  const handleSignUp = async (
    email: string, 
    password: string, 
    metadata: Record<string, any> = {}
  ): Promise<AuthResponse> => {
    setError(null);
    setLoading(true);
    
    try {
      const { data, error } = await signUp(email, password, metadata);
      
      if (error) {
        setError(error.message);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (err: any) {
      setError(err.message);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Sign in function
  const handleSignIn = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    
    try {
      const { data, error } = await signIn(email, password);
      
      if (error) {
        setError(error.message);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (err: any) {
      setError(err.message);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const handleSignOut = async () => {
    setError(null);
    
    try {
      const { error } = await signOut();
      
      if (error) {
        setError(error.message);
        return { success: false, error };
      }
      
      setUser(null);
      setProfile(null);
      clearCachedProfile(); // Clear localStorage cache on logout
      profileCache.current.clear(); // Clear memory cache
      return { success: true };
    } catch (err: any) {
      setError(err.message);
      return { success: false, error: err };
    }
  };

  // Check if user has a specific role
  const hasRole = (role: string): boolean => {
    return profile?.role === role;
  };

  // Check if user is admin
  const isAdmin = (): boolean => {
    // Localhost development bypass - always admin on localhost
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return true;
    }
    
    // Allow these emails as admin regardless of profile role (case-insensitive)
    const adminEmails = [
      'jason@blissbunkbeds.com',
      'admin@blissbunkbeds.com',
    ];
    const userEmailLower = user?.email?.toLowerCase();
    if (userEmailLower && adminEmails.includes(userEmailLower)) {
      return true;
    }
    return profile?.role === 'admin' || profile?.role === 'owner';
  };

  // Check if user is a partner
  const isPartner = (): boolean => {
    return profile?.role === 'partner';
  };

  const value: AuthContextType = {
    user,
    profile,
    loading: loading || !sessionReady || (!!user && resolvedProfileId !== user.id),
    error,
    signUp: handleSignUp,
    signIn: handleSignIn,
    signOut: handleSignOut,
    hasRole,
    isAdmin,
    isPartner,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;

